import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useLocation } from "react-router-dom";
import { isSafeRedirect } from "@/lib/utils";
import { useAuthStore } from "@/stores/authStore";
import { Button } from "@/components/ui";
import {
  Mail,
  Lock,
  User,
  AlertCircle,
  ArrowRight,
  Info,
  Check,
  Eye,
  EyeOff,
} from "lucide-react";
import VerifyEmailPage from "./VerifyEmailPage";
import InviteBanner from "@/components/auth/InviteBanner";
import { TERMS_VERSION, PRIVACY_VERSION } from "@/pages/legal/legalMeta";

// Validador reusable: solo letras (incl. acentos), espacios, guiones y apostrofes.
const nameRegex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s'\-]+$/;
const nameError = "Solo letras, espacios, guiones y apóstrofes";

const registerSchema = z
  .object({
    firstName: z
      .string()
      .trim()
      .min(2, "Mínimo 2 caracteres")
      .max(40, "Máximo 40 caracteres")
      .regex(nameRegex, nameError),
    secondName: z
      .string()
      .trim()
      .max(40, "Máximo 40 caracteres")
      .regex(nameRegex, nameError)
      .optional()
      .or(z.literal("")),
    lastName1: z
      .string()
      .trim()
      .min(2, "Mínimo 2 caracteres")
      .max(40, "Máximo 40 caracteres")
      .regex(nameRegex, nameError),
    lastName2: z
      .string()
      .trim()
      .max(40, "Máximo 40 caracteres")
      .regex(nameRegex, nameError)
      .optional()
      .or(z.literal("")),
    email: z.string().email("Email inválido"),
    password: z
      .string()
      .min(6, "Mínimo 6 caracteres")
      .regex(/[A-Z]/, "Debe incluir una mayúscula")
      .regex(/[a-z]/, "Debe incluir una minúscula")
      .regex(/[0-9]/, "Debe incluir un número"),
    confirmPassword: z.string(),
    acceptTerms: z.literal(true, {
      errorMap: () => ({
        message:
          "Debes aceptar los Términos y la Política de Privacidad para continuar",
      }),
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Las contraseñas no coinciden",
    path: ["confirmPassword"],
  });

type RegisterForm = z.infer<typeof registerSchema>;

/**
 * Concatena los 4 campos en un displayName limpio.
 * Maneja el caso de personas con un solo nombre / un solo apellido.
 */
function buildDisplayName(data: RegisterForm): string {
  return [data.firstName, data.secondName, data.lastName1, data.lastName2]
    .map((s) => (s ?? "").trim())
    .filter(Boolean)
    .join(" ");
}

export default function RegisterPage() {
  const { signUp, error, clearError, emailNotVerified } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const location = useLocation();
  const redirectParam = new URLSearchParams(location.search).get("redirect");
  const decodedRedirect = redirectParam
    ? decodeURIComponent(redirectParam)
    : "";
  const redirectAfterLogin = isSafeRedirect(decodedRedirect)
    ? decodedRedirect
    : "/dashboard";

  // Limpiar errores previos al montar la página
  useEffect(() => {
    clearError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    mode: "onTouched",
  });

  const password = watch("password", "");
  const confirmPassword = watch("confirmPassword", "");

  const passwordRequirements = [
    { label: "Mínimo 6 caracteres", met: password.length >= 6 },
    { label: "Una mayúscula", met: /[A-Z]/.test(password) },
    { label: "Una minúscula", met: /[a-z]/.test(password) },
    { label: "Un número", met: /[0-9]/.test(password) },
  ];

  const onSubmit = async (data: RegisterForm) => {
    setLoading(true);
    clearError();

    try {
      const displayName = buildDisplayName(data);
      await signUp(data.email, data.password, displayName, {
        termsVersion: TERMS_VERSION,
        privacyVersion: PRIVACY_VERSION,
        acceptedFromUrl: window.location.href,
        userAgent:
          typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
      });
      setRegisteredEmail(data.email);
    } catch {
      // Error is handled in the store
    } finally {
      setLoading(false);
    }
  };

  // Mostrar página de verificación de email después del registro
  if (registeredEmail || emailNotVerified) {
    return (
      <VerifyEmailPage
        email={registeredEmail || undefined}
        redirect={redirectAfterLogin}
      />
    );
  }

  return (
    <div>
      <InviteBanner />
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Crear cuenta</h2>
        <p className="text-gray-500 mt-2">Regístrate para acceder al sistema</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Nombres */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Primer nombre <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                autoComplete="given-name"
                placeholder="Ej: María"
                className={`w-full pl-12 pr-4 py-3 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary-500/20 ${
                  errors.firstName
                    ? "border-red-300 focus:border-red-500"
                    : "border-gray-200 focus:border-primary-500 hover:border-gray-300"
                }`}
                {...register("firstName")}
              />
            </div>
            {errors.firstName && (
              <p className="text-sm text-red-500 mt-2 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.firstName.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Segundo nombre{" "}
              <span className="text-gray-400 font-normal text-xs">
                (opcional)
              </span>
            </label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                autoComplete="additional-name"
                placeholder="Ej: Fernanda"
                className={`w-full pl-12 pr-4 py-3 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary-500/20 ${
                  errors.secondName
                    ? "border-red-300 focus:border-red-500"
                    : "border-gray-200 focus:border-primary-500 hover:border-gray-300"
                }`}
                {...register("secondName")}
              />
            </div>
            {errors.secondName && (
              <p className="text-sm text-red-500 mt-2 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.secondName.message}
              </p>
            )}
          </div>
        </div>

        {/* Apellidos */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Primer apellido <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                autoComplete="family-name"
                placeholder="Ej: Gómez"
                className={`w-full pl-12 pr-4 py-3 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary-500/20 ${
                  errors.lastName1
                    ? "border-red-300 focus:border-red-500"
                    : "border-gray-200 focus:border-primary-500 hover:border-gray-300"
                }`}
                {...register("lastName1")}
              />
            </div>
            {errors.lastName1 && (
              <p className="text-sm text-red-500 mt-2 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.lastName1.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Segundo apellido{" "}
              <span className="text-gray-400 font-normal text-xs">
                (opcional)
              </span>
            </label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Ej: Pérez"
                className={`w-full pl-12 pr-4 py-3 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary-500/20 ${
                  errors.lastName2
                    ? "border-red-300 focus:border-red-500"
                    : "border-gray-200 focus:border-primary-500 hover:border-gray-300"
                }`}
                {...register("lastName2")}
              />
            </div>
            {errors.lastName2 && (
              <p className="text-sm text-red-500 mt-2 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.lastName2.message}
              </p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Correo electrónico
          </label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="email"
              autoComplete="email"
              placeholder="tu@correo.com"
              className={`w-full pl-12 pr-4 py-3 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary-500/20 ${
                errors.email
                  ? "border-red-300 focus:border-red-500"
                  : "border-gray-200 focus:border-primary-500 hover:border-gray-300"
              }`}
              {...register("email")}
            />
          </div>
          {errors.email && (
            <p className="text-sm text-red-500 mt-2 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.email.message}
            </p>
          )}
          {/* F12-QW11: la raíz de los casos de soporte más caros es gente que
              se registra con el correo de su pareja/hijo. Advertirlo aquí sale
              mucho más barato que migrar la ficha después. */}
          <p className="text-xs text-gray-500 mt-2">
            Este será tu usuario para iniciar sesión.{" "}
            <span className="font-semibold text-gray-700">
              Usa tu correo personal
            </span>
            , no el de tu pareja, hijo o club: tus datos y permisos quedan
            atados a él.
          </p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Contraseña
          </label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder="••••••••"
              className={`w-full pl-12 pr-12 py-3 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary-500/20 ${
                errors.password
                  ? "border-red-300 focus:border-red-500"
                  : "border-gray-200 focus:border-primary-500 hover:border-gray-300"
              }`}
              {...register("password")}
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="text-sm text-red-500 mt-2 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.password.message}
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {passwordRequirements.map((req, index) => (
              <span
                key={index}
                className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 transition-colors duration-200 ${
                  req.met
                    ? "bg-green-100 text-green-700"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                {req.met && <Check className="w-3 h-3" />}
                {req.label}
              </span>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Confirmar contraseña
          </label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type={showConfirmPassword ? "text" : "password"}
              autoComplete="new-password"
              placeholder="••••••••"
              className={`w-full pl-12 pr-12 py-3 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary-500/20 ${
                errors.confirmPassword
                  ? "border-red-300 focus:border-red-500"
                  : confirmPassword && confirmPassword === password
                    ? "border-green-400 focus:border-green-500"
                    : "border-gray-200 focus:border-primary-500 hover:border-gray-300"
              }`}
              {...register("confirmPassword")}
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowConfirmPassword((v) => !v)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showConfirmPassword ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          </div>
          {confirmPassword &&
            confirmPassword === password &&
            !errors.confirmPassword && (
              <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                <Check className="w-4 h-4" />
                Las contraseñas coinciden
              </p>
            )}
          {errors.confirmPassword && (
            <p className="text-sm text-red-500 mt-2 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.confirmPassword.message}
            </p>
          )}
        </div>

        {error && (
          <div className="p-4 rounded-xl bg-gradient-to-r from-red-50 to-red-100/50 border-2 border-red-100 text-red-700 text-sm flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-red-400 to-red-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
              <AlertCircle className="w-4 h-4 text-white" />
            </div>
            <span>{error}</span>
          </div>
        )}

        {/* Aceptación de términos legales (Habeas Data - Ley 1581/2012) */}
        <div
          className={`p-4 rounded-xl border-2 transition-colors ${
            errors.acceptTerms
              ? "border-red-300 bg-red-50/50"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 w-5 h-5 rounded border-2 border-gray-300 text-primary-600 focus:ring-4 focus:ring-primary-500/20 cursor-pointer flex-shrink-0"
              {...register("acceptTerms")}
            />
            <span className="text-sm text-gray-700 leading-relaxed">
              He leído y acepto los{" "}
              <Link
                to="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 font-semibold underline"
              >
                Términos y Condiciones
              </Link>{" "}
              y la{" "}
              <Link
                to="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 font-semibold underline"
              >
                Política de Privacidad y Tratamiento de Datos
              </Link>{" "}
              conforme a la Ley 1581 de 2012 (Habeas Data).{" "}
              <span className="text-gray-500">
                Si registras menores de edad, declaras contar con autorización
                escrita de sus padres o representantes legales.
              </span>
            </span>
          </label>
          {errors.acceptTerms && (
            <p className="text-sm text-red-500 mt-2 ml-8 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.acceptTerms.message}
            </p>
          )}
        </div>

        <Button
          type="submit"
          className="w-full py-3 text-base font-semibold"
          loading={loading}
        >
          Crear cuenta
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </form>

      <div className="mt-6 text-center">
        <p className="text-sm text-gray-500">
          ¿Ya tienes cuenta?{" "}
          <Link
            to="/login"
            className="text-primary-600 hover:text-primary-700 font-semibold transition-colors duration-200"
          >
            Inicia sesión
          </Link>
        </p>
      </div>

      <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-primary-50 border-2 border-blue-100 rounded-xl">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
            <Info className="w-4 h-4 text-white" />
          </div>
          <p className="text-sm text-blue-700">
            <strong>Nota:</strong> Después de registrarte, un administrador de
            tu asociación deberá activar tu cuenta y asignarte los permisos
            correspondientes.
          </p>
        </div>
      </div>
    </div>
  );
}
